import axios from 'axios';

// Main Attractis backend – used for authentication
const ATTRACTIS_API_BASE = process.env.REACT_APP_ATTRACTIS_API_URL || 'http://localhost:3005/api';

// Table Vision local backend – used for table management, cameras, AI
const TABLE_API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000/api';

const TOKEN_KEY = 'attractis_token';

/** Use Table backend proxy so hls.js avoids browser CORS on third-party CDNs (e.g. ipcamlive). */
export function streamHlsProxyUrl(upstreamUrl: string): string {
  const base = TABLE_API_BASE.replace(/\/$/, '');
  return `${base}/stream/hls?u=${encodeURIComponent(upstreamUrl)}`;
}

// ── Auth API (Attractis backend) ─────────────────────────────────────────────

export const authApi = axios.create({ baseURL: ATTRACTIS_API_BASE });

authApi.interceptors.request.use((config) => {
  if (!config.headers) config.headers = {} as any;
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && !(config.headers as any)['authorization']) {
    (config.headers as any)['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

export async function loginAttracts(email: string, password: string) {
  const res = await authApi.post('/auth/login', { email, password });
  return res.data as { token: string; user: any; businesses?: any[] };
}

// ── Table API (Table Vision backend) ─────────────────────────────────────────

export const api = axios.create({ baseURL: TABLE_API_BASE });

export function setAuthToken(token: string | null): void {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
}

api.interceptors.request.use((config) => {
  if (!config.headers) config.headers = {} as any;
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && !(config.headers as any)['authorization']) {
    (config.headers as any)['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    if (error?.response?.status === 401) {
      try { localStorage.removeItem(TOKEN_KEY); } catch {}
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Convenience wrappers kept for existing callers
export async function login(email: string, password: string) {
  return loginAttracts(email, password);
}

export async function register(businessName: string, email: string, password: string) {
  const res = await api.post('/auth/register', { businessName, email, password });
  return res.data as { token: string; user: any; business: any };
}

// Locations
export async function getLocations() {
  const res = await api.get('/locations');
  return res.data as Array<{ id: number; name: string }>;
}
export async function createLocation(name: string) {
  const res = await api.post('/locations', { name });
  return res.data as { id: number; name: string };
}

export type Camera = { id: number; name: string; streamUrl: string; locationId: number };

// Cameras
export async function getCameras() {
  const res = await api.get('/cameras');
  return res.data as Camera[];
}
export async function createCamera(locationId: number, name: string, streamUrl: string) {
  const res = await api.post('/cameras', { locationId, name, streamUrl });
  return res.data as Camera;
}
export async function updateCamera(id: number, data: { name?: string; streamUrl?: string }) {
  const res = await api.patch(`/cameras/${id}`, data);
  return res.data as Camera;
}
export async function deleteCamera(id: number): Promise<void> {
  await api.delete(`/cameras/${id}`);
}
export async function startCameraAI(id: number) {
  const res = await api.post(`/cameras/${id}/start`);
  return res.data as { started: boolean; streamUrl: string };
}
export async function stopCameraAI(id: number) {
  const res = await api.post(`/cameras/${id}/stop`);
  return res.data as { stopped: boolean };
}
export async function getActiveStreams() {
  const res = await api.get('/cameras/active');
  return res.data as { activeStreams: string[] };
}
